/**
 * Columns for the GRN drill-downs (Qty Received, Total Value, No. of GRNs,
 * Ontime / Late / Early). Customer review 11 Sep (items 5, 8, 15).
 *
 * Part Number and Description read the row's FLAT `part_number` /
 * `part_description` — the list serializer never emits the nested
 * `po_line_details` the old table read, which is why every row showed "-".
 * Inspection is deliberately absent: it is not a platform concept.
 *
 * "Converted Total Value" is the value frozen at receipt — the figure the
 * Total Value cell sums — so the export reconciles to the cell. It need not
 * equal Qty x Converted Unit Price: the line's converted price is today's, the
 * frozen value keeps the receipt-date price and rate (METRIC-DEFINITIONS M6).
 */
import { Badge } from '../../../components/ui/badge';
import GrnLink from '../../../components/common/GrnLink';
import PoHeaderLink from '../../../components/common/PoHeaderLink';
import PoLineLink from '../../../components/common/PoLineLink';
import { GRNPerformanceDrillRow } from '../../../lib/api';
import { DrilldownColumn, DrilldownFormatters, dateOf } from './columns';
import { ColumnOptions } from './columnsFor';

function onTimeClass(status?: string): string {
  if (status === 'on_time') return 'bg-green-100 text-green-800';
  if (status === 'late') return 'bg-red-100 text-red-800';
  if (status === 'early') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
}

type Row = GRNPerformanceDrillRow;

export function grnColumns(f: DrilldownFormatters, options: ColumnOptions): DrilldownColumn<Row>[] {
  // The customer's reporting-currency view; suppliers see transaction currency only.
  const converted: DrilldownColumn<Row>[] = options.isSupplier ? [] : [
    {
      key: 'unit_price_converted', label: 'Converted Unit Price', kind: 'money', align: 'right',
      title: "The PO line's unit price in the customer's reporting currency",
      value: (g) => g.unit_price_converted ?? null, currency: (g) => g.converted_currency,
    },
    {
      key: 'received_value', label: 'Converted Total Value', kind: 'money', align: 'right',
      title: 'Value frozen at receipt in the reporting currency — the figure the Total Value cell sums',
      value: (g) => g.received_value ?? null, currency: (g) => g.converted_currency,
    },
  ];

  return [
    // CR-1154: GRN number links to its delivery detail; PO number and line
    // deep-link to the line item.
    {
      key: 'grn_number', label: 'GRN Number', className: 'font-medium',
      value: (g) => g.grn_number,
      render: (g) => <GrnLink grnNumber={g.grn_number} />,
    },
    {
      key: 'po_number', label: 'PO Number',
      value: (g) => g.po_number ?? null,
      render: (g) => <PoHeaderLink poNumber={g.po_number}>{g.po_number || '-'}</PoHeaderLink>,
    },
    {
      key: 'line_number', label: 'Line #',
      value: (g) => g.line_number ?? null,
      render: (g) => <PoLineLink poLineId={g.po_line}>{g.line_number ?? '-'}</PoLineLink>,
    },
    // The PO's own customer site (review item 3), not the viewer's site.
    { key: 'customer_name', label: 'Customer', value: (g) => g.customer_name ?? null },
    { key: 'supplier_group', label: 'Supplier Group', value: (g) => g.supplier_group ?? null },
    { key: 'supplier_name', label: 'Supplier Site', value: (g) => g.supplier_name ?? null },
    { key: 'part_number', label: 'Part Number', value: (g) => g.part_number ?? null },
    { key: 'part_description', label: 'Description', truncate: true, value: (g) => g.part_description ?? null },
    { key: 'quantity_ordered', label: 'Qty Ordered', kind: 'qty', align: 'right', value: (g) => g.quantity_ordered ?? null },
    { key: 'quantity_received', label: 'Qty Received', kind: 'qty', align: 'right', value: (g) => g.quantity_received },
    { key: 'quantity_accepted', label: 'Qty Accepted', kind: 'qty', align: 'right', className: 'text-green-600', value: (g) => g.quantity_accepted },
    { key: 'quantity_rejected', label: 'Qty Rejected', kind: 'qty', align: 'right', className: 'text-red-600', value: (g) => g.quantity_rejected },
    { key: 'unit_price', label: 'Unit Price', kind: 'money', align: 'right', value: (g) => g.unit_price ?? null, currency: (g) => g.currency },
    { key: 'currency', label: 'Currency', value: (g) => g.currency ?? null },
    {
      key: 'total_value', label: 'Total Value', kind: 'money', align: 'right',
      title: 'Qty Received x Unit Price, in the PO currency',
      value: (g) => g.total_value ?? null, currency: (g) => g.currency,
    },
    ...converted,
    {
      key: 'invoice_net_amount', label: 'Invoice Value', kind: 'money', align: 'right',
      title: 'Net amount of the invoice matched to this receipt (blank until invoiced)',
      value: (g) => g.invoice_net_amount ?? null, currency: (g) => g.currency,
    },
    // Customer review 11 Sep (stream I, from E): ODD is THE verdict anchor
    // (attributed slot -> line ODD -> delivery date) — the date this row's On
    // Time Status was measured against, and what the Deliveries grid's ODD
    // shows. It used to print the re-planned po_line.delivery_date, so a row
    // could read "Early" beside an ODD it was plainly after.
    {
      key: 'verdict_anchor_date', label: 'ODD',
      title: 'The date the On Time Status was measured against (schedule slot, else the line ODD, else its delivery date)',
      value: (g) => dateOf(f.formatDate, g.verdict_anchor_date ?? g.original_delivery_date),
    },
    { key: 'receipt_date', label: 'Delivery Date', value: (g) => dateOf(f.formatDate, g.receipt_date) },
    {
      key: 'on_time_status', label: 'On Time Status', align: 'center',
      value: (g) => g.on_time_status_display || g.on_time_status || null,
      render: (g) => g.on_time_status
        ? <Badge className={onTimeClass(g.on_time_status)}>{g.on_time_status_display || g.on_time_status}</Badge>
        : <span className="text-muted-foreground">-</span>,
    },
    { key: 'updated_at', label: 'Update Time', value: (g) => dateOf(f.formatDateTime, g.updated_at) },
  ];
}
